import React, { useState } from 'react';
import { checkinTicket } from '../apiEndpoints';
import { useQueryClient } from '@tanstack/react-query';
import { Modal, ModalContent, ModalHeader, ModalBody, Button } from '@heroui/react';
import useIsMobile from '../hooks/useIsMobile';
import { btnAccent } from '@/components/ui/design';

interface TicketCheckInModalProps {
    isOpen: boolean;
    onClose: () => void;
    order: any;
    ticket: any;
    eventId: string;
}

export default function TicketCheckInModal({ isOpen, onClose, order, ticket, eventId }: TicketCheckInModalProps) {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const queryClient = useQueryClient();

    const handleManualCheckIn = async () => {
        setIsLoading(true);
        setError('');
        setSuccess('');

        try {
            // Use the ticket's QR code for manual check-in
            const ticketQrCode = ticket.qrCode || ticket.ticketId?.qrCode;
            if (!ticketQrCode) {
                throw new Error('No QR code found for this ticket');
            }

            await checkinTicket(eventId, order.hashedUniqueId || order.orderId, ticketQrCode, 'manual');
            setSuccess('Ticket checked in successfully!');

            // Refresh the orders data
            queryClient.invalidateQueries({ queryKey: ['eventOrders', eventId] });

            // Close modal after 2 seconds
            setTimeout(() => {
                onClose();
                setSuccess('');
            }, 2000);
        } catch (err: any) {
            setError(err.response?.data?.message || err.message || 'Failed to check in ticket');
        } finally {
            setIsLoading(false);
        }
    };



    const isMobile = useIsMobile();

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size={isMobile ? 'full' : 'md'}
            scrollBehavior="inside"
            classNames={{
                base: 'bg-white dark:bg-nightSurface rounded-none md:rounded-[28px] shadow-none md:ring-1 md:ring-black/[0.06] md:dark:ring-white/10 h-full md:h-auto',
                wrapper: 'p-0 md:p-4 z-[100000]',
                header: 'px-6 pt-6',
                body: 'px-6',
                closeButton: 'right-4 top-4 rounded-full hover:bg-grey99 dark:hover:bg-white/10',
                backdrop: 'bg-black/50',
            }}
        >
            <ModalContent>
                <ModalHeader>
                    <h2 className="text-2xl font-bold tracking-[-0.03em] text-grey10 dark:text-white">Check-in Ticket</h2>
                </ModalHeader>

                {/* Ticket Info */}
                <div className="px-6 pt-2">
                    <div className="space-y-3 rounded-2xl bg-grey99 p-4 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06]">
                        <div className="flex justify-between gap-4">
                            <span className="shrink-0 text-sm text-grey40 dark:text-nightMuted">Attendee:</span>
                            <span className="min-w-0 break-all text-right text-sm font-semibold text-grey10 dark:text-white">
                                {order.customerName || order.guestName || order.attendeeName || 'N/A'}
                            </span>
                        </div>
                        <div className="flex justify-between gap-4">
                            <span className="shrink-0 text-sm text-grey40 dark:text-nightMuted">Ticket:</span>
                            <span className="min-w-0 break-all text-right text-sm font-semibold text-grey10 dark:text-white">
                                {ticket.ticketId?.name || 'Ticket'}
                            </span>
                        </div>
                        <div className="flex justify-between gap-4">
                            <span className="shrink-0 text-sm text-grey40 dark:text-nightMuted">Order ID:</span>
                            <span className="min-w-0 break-all text-right text-sm font-semibold text-grey10 dark:text-white">
                                {order.hashedUniqueId || order.orderId}
                            </span>
                        </div>
                        <div className="flex justify-between gap-4">
                            <span className="shrink-0 text-sm text-grey40 dark:text-nightMuted">Ticket ID:</span>
                            <span className="min-w-0 break-all text-right text-sm font-semibold text-grey10 dark:text-white">
                                {ticket.qrCode || ticket.ticketId?.qrCode || 'No QR code available'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <ModalBody className="py-6">
                    <div className="text-center">
                        <p className="text-base text-grey40 dark:text-nightMuted mb-4">
                            Are you sure you want to check in this ticket?
                        </p>

                        <Button
                            onPress={handleManualCheckIn}
                            isLoading={isLoading}
                            className={`${btnAccent} w-full`}
                        >
                            Confirm Check-in
                        </Button>
                    </div>

                    {/* Error/Success Messages */}
                    {error && (
                        <div className="rounded-full bg-error95 px-4 py-2.5 text-center dark:bg-error50/15">
                            <p className="text-sm font-semibold text-error40 dark:text-error70">{error}</p>
                        </div>
                    )}

                    {success && (
                        <div className="rounded-full bg-success95 px-4 py-2.5 text-center dark:bg-success50/15">
                            <p className="text-sm font-semibold text-success30 dark:text-success60">{success}</p>
                        </div>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
} 